import React, { useState, useEffect } from "react";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate } from "../../../helpers/Utils";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { CycleIconSvg } from "../../../svgFiles/CycleIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { TrendingUpIconSvg } from "../../../svgFiles/TrendingUpIconSvg";
import { Invoice } from "../../../svgFiles/Invoice";
import { GreenTickSvg } from "../../../svgFiles/GreenTickSvg";
import { AlertSvg } from "../../../svgFiles/AlertSvg";
import { InvoicesAttachedSvg } from "../../../svgFiles/InvoicesAttachedSvg";
import { ReceiptsAttachedSvg } from "../../../svgFiles/ReceiptsAttachedSvg";
import { MissingInvoiceSvg } from "../../../svgFiles/MissingInvoiceSvg";
import { RegisteredBillsSvg } from "../../../svgFiles/RegisteredBillsSvg";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useGetExpenseSummaryQuery } from "../expenseApi";

const ExpenseDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const startOfYear = getCurrentDate("YYYY") + "-01-01";
  const [dateRange, setDateRange] = useState({
    startDate: startOfYear,
    endDate: currentDate,
  });

  // Fetch data for all tabs
  const { data: overviewResponse, isLoading: overviewLoading, isFetching: overviewFetching, refetch: overviewRefetch } = useGetExpenseSummaryQuery({
    tab: "overview",
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
  });

  const { data: statusResponse, isLoading: statusLoading, isFetching: statusFetching, refetch: statusRefetch } = useGetExpenseSummaryQuery({
    tab: "status_breakdown",
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
  });

  const { data: documentResponse, isLoading: documentLoading, isFetching: documentFetching, refetch: documentRefetch } = useGetExpenseSummaryQuery({
    tab: "document_coverage",
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
  });

  const { data: vendorsResponse, isLoading: vendorsLoading, isFetching: vendorsFetching, refetch: vendorsRefetch } = useGetExpenseSummaryQuery({
    tab: "top_five_vendors_by_spend",
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
  });

  const { data: trendResponse, isLoading: trendLoading, isFetching: trendFetching, refetch: trendRefetch } = useGetExpenseSummaryQuery({
    tab: "monthly_spend_trend",
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
  });

  useEffect(() => {
    overviewRefetch();
    statusRefetch();
    documentRefetch();
    vendorsRefetch();
    trendRefetch();
  }, []);

  const isLoading = overviewLoading || statusLoading || documentLoading || vendorsLoading || trendLoading || overviewFetching || statusFetching || documentFetching || vendorsFetching || trendFetching;
  const hasError = false;

  const handleApplyRange = ({ startDate, endDate }) => {
    setDateRange({ startDate, endDate });
  };

  // Parse numeric string values from API
  const parseMoneyValue = (value) => {
    if (typeof value === "string") {
      return parseFloat(value.replace(/,/g, ""));
    }
    return value || 0;
  };

  // ── Overview Metric Cards ────────────────────────────────────
  const overviewData = overviewResponse?.data || {};
  const overviewMetrics = [
    {
      label: _t("total_bills_value", "Total Bills Value"),
      value: parseMoneyValue(overviewData.total_bills_value),
      color: "#3b82f6",
    },
    {
      label: _t("amount_paid", "Amount Paid"),
      value: parseMoneyValue(overviewData.amount_paid),
      color: "#10b981",
    },
    {
      label: _t("outstanding_pending", "Outstanding / Pending"),
      value: parseMoneyValue(overviewData.outstanding_pending),
      color: "#f59e0b",
    },
    {
      label: _t("overdue_amount", "Overdue Amount"),
      value: parseMoneyValue(overviewData.overdue_amount),
      color: "#ef4444",
    },
  ];

  // ── Status Breakdown Items ───────────────────────────────────
  const statusData = statusResponse?.data || {};
  const statusBreakdownItems = [
    { label: _t("paid", "Paid"), value: statusData.paid || 0, unit: "", color: "#10b981" },
    { label: _t("pending", "Pending"), value: statusData.pending || 0, unit: "", color: "#f59e0b" },
    { label: _t("overdue", "Overdue"), value: statusData.overdue || 0, unit: "", color: "#ef4444" },
    { label: _t("draft", "Draft"), value: statusData.draft || 0, unit: "", color: "#6b7280" },
  ];

  // ── Top Vendors Items ────────────────────────────────────────
  // Calculate total spend from vendors list for percentage calculation
  const vendorsListData = Array.isArray(vendorsResponse?.data) ? vendorsResponse.data : [];
  const totalVendorSpend = vendorsListData.reduce(
    (sum, vendor) => sum + parseMoneyValue(vendor.total_spend),
    0
  );
  const topVendorsItems = vendorsListData.map((vendor) => ({
    label: vendor.vendor_name,
    value: vendor.total_spend,
    percentage: totalVendorSpend > 0
      ? Math.round((parseMoneyValue(vendor.total_spend) / totalVendorSpend) * 100)
      : 0,
  }));

  // ── Monthly Spend Trend (SINGLE SOURCE OF TRUTH) ────────────
  const trendData = Array.isArray(trendResponse?.data) ? trendResponse.data : [];

  const monthlySpendData = trendData.map((item) => ({
    month: item.month,
    year: item.year,
    value: parseMoneyValue(item.total_spend),
  }));

  // ── Derived Chart Metrics from Single Source ────────────────
  const getPeakMonth = () => {
    let maxValue = 0;
    let peakMonth = "";
    let peakYear = "";
    monthlySpendData.forEach((item) => {
      if (item.value > maxValue) {
        maxValue = item.value;
        peakMonth = item.month;
        peakYear = item.year;
      }
    });
    return { month: peakMonth, year: peakYear, value: maxValue };
  };

  const getSixMonthAverage = () => {
    const lastSixMonths = monthlySpendData.slice(-6);
    const sum = lastSixMonths.reduce((acc, item) => acc + item.value, 0);
    return Math.round(sum / 6);
  };

  const getDateRangeLabel = () => {
    if (!trendData || trendData.length === 0) {
      return dateRange.startDate + " - " + dateRange.endDate;
    }
    const firstMonth = trendData[0];
    const lastMonth = trendData[trendData.length - 1];
    
    // If start and end dates are the same, show just one month
    if (dateRange.startDate === dateRange.endDate) {
      return `${_t(firstMonth.month ,firstMonth.month)} ${firstMonth.year}`;
    }
    
    return `${_t(firstMonth.month , firstMonth.month)} ${firstMonth.year} - ${_t(lastMonth.month ,lastMonth.month)} ${lastMonth.year}`;
  };

  const peakData = getPeakMonth();
  const sixMonthAvg = getSixMonthAverage();

  // ── Document Coverage Items ──────────────────────────────────
  const documentData = documentResponse?.data || {};
  const documentCoverageItems = [
    {
      label: _t("invoices_attached", "Invoices Attached"),
      value: documentData.invoices_attached || 0,
      color: "#d95140",
      icon: <InvoicesAttachedSvg />,
    },
    {
      label: _t("receipts_attached", "Receipts Attached"),
      value: documentData.receipts_attached || 0,
      color: "#3b82f6",
      icon: <ReceiptsAttachedSvg />,
    },
    {
      label: _t("missing_invoice", "Missing Invoice"),
      value: documentData.missing_invoice || 0,
      color: "#ef4444",
      icon: <MissingInvoiceSvg />,
    },
    {
      label: _t("registered_bills", "Registered Bills"),
      value: documentData.registered_bills || 0,
      color: "#10b981",
      icon: <RegisteredBillsSvg />,
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="receiving_dashboard_toolbar align-items-center">
        <div>
          <h5 className="receiving_dashboard_toolbar_title m-0">
            {_t("bills_and_exppense_overview", "Bills & Expense Overview")}
          </h5>
        </div>
        <div className="receiving_filter_group">
          <DateRangeFilter
            onApplyRange={handleApplyRange}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            triggerId="expense-date-range-filter"
          />
        </div>
      </div>

      {/* ── Loading State ──────────────────────────────────────── */}
      {isLoading && (
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      )}

      {/* ── Error State ────────────────────────────────────────── */}
      {hasError && (
        <div
          style={{
            backgroundColor: "#fee2e2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            padding: "16px",
            color: "#991b1b",
            marginBottom: "16px",
          }}
        >
          {_t("error_loading_data", "Error loading expense data")}
        </div>
      )}

      {/* ── Content (only show when loaded) ──────────────────────── */}
      {!isLoading && !hasError && (
        <>
          <DashboardSection title={_t("expense_summary", "Expense Summary")} displayAccordion={false}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "16px",
          }}
        >
          {overviewMetrics.map((metric, i) => (
            <div
              key={i}
              style={{
                backgroundColor: "#f9fafb",
                border: "1px solid #f0f1f3",
                borderRadius: "8px",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#667085",
                  textTransform: "uppercase",
                }}
              >
                {metric.label}
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: metric.color,
                }}
              >
                ${metric.value.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </DashboardSection>

      {/* ── Status Breakdown - Document Coverage - Top Vendors ──────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "16px",
          marginTop: "24px",
        }}
      >
        {/* Status Breakdown */}
        <DashboardSection
          title={_t("status_breakdown", "Status Breakdown")}
          displayAccordion={false}
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {statusBreakdownItems.map((item, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingBottom: "12px",
                  borderBottom:
                    i < statusBreakdownItems.length - 1
                      ? "1px solid #f0f1f3"
                      : "none",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: item.color,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#1d1f2c",
                      fontWeight: "500",
                    }}
                  >
                    {item.label}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1d1f2c",
                  }}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </DashboardSection>

        {/* Document Coverage */}
        <DashboardSection
          title={_t("document_coverage", "Document Coverage")}
          displayAccordion={false}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "12px",
            }}
          >
            {documentCoverageItems.map((item, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: "#f9fafb",
                  border: "1px solid #f0f1f3",
                  borderRadius: "8px",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "12px",
                  transition: "all 0.2s ease",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: `${item.color}20`,
                      borderRadius: "6px",
                      color: item.color,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: item.color,
                    }}
                  >
                    {item.value}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#667085",
                  }}
                >
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </DashboardSection>

        {/* Top Vendors */}
        <DashboardSection
          title={_t("top_vendors_by_spend", "Top Vendors by Spend")}
          displayAccordion={false}
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {topVendorsItems.length > 0 ? (
              topVendorsItems.map((vendor, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: "12px",
                    borderBottom:
                      i < topVendorsItems.length - 1
                        ? "1px solid #f0f1f3"
                        : "none",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#1d1f2c",
                      }}
                    >
                      {vendor.label}
                    </div>
                    <div style={{ fontSize: "11px", color: "#667085" }}>
                      {vendor.percentage}% {_t("of_expenses", "of expenses")}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#1d1f2c",
                    }}
                  >
                    ${vendor.value}
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#9ca3af",
                  fontSize: "14px",
                }}
              >
                {_t("no_vendors_found", "No vendors found")}
              </div>
            )}
          </div>
        </DashboardSection>
      </div>

      {/* ── Monthly Spend Trend (ACCURATE & CONSISTENT) ────────────── */}
      <DashboardSection
        title={
          <span className="d-flex align-items-center gap-2">
            <TrendingUpIconSvg size={18} color="#fb923c" />
            {_t("monthly_spend_trend", "Monthly Spend Trend")}
          </span>
        }
        displayAccordion={false}
      >
        <div className="cmn_card" style={{ backgroundColor: "#fff" }}>
          <div style={{ padding: "16px" }}>
            {/* Chart Summary Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              {/* Peak Month */}
              <div
                style={{
                  backgroundColor: "#f9fafb",
                  border: "1px solid #f0f1f3",
                  borderRadius: "8px",
                  padding: "14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "600",
                    color: "#667085",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {_t("chart_peak", "Chart Peak")}
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#fb923c",
                  }}
                >
                  {_t(peakData.month,peakData.month)} {peakData.year} • ${peakData.value.toLocaleString()}
                </div>
              </div>

              {/* Date Range */}
              <div
                style={{
                  backgroundColor: "#f9fafb",
                  border: "1px solid #f0f1f3",
                  borderRadius: "8px",
                  padding: "14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "600",
                    color: "#667085",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {_t("period", "Period")}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "#1d1f2c",
                  }}
                >
                  {getDateRangeLabel()}
                </div>
              </div>

              {/* 6-Month Average */}
              <div
                style={{
                  backgroundColor: "#f9fafb",
                  border: "1px solid #f0f1f3",
                  borderRadius: "8px",
                  padding: "14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "600",
                    color: "#667085",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {_t("six_month_avg", "6-Month Avg")}
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#10b981",
                  }}
                >
                  ${sixMonthAvg.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Chart */}
            <div style={{ marginTop: "20px" }}>
              <HighchartsReact
                highcharts={Highcharts}
                options={{
                  chart: {
                    type: "column",
                    backgroundColor: "transparent",
                    borderRadius: 0,
                    margin: [10, 30, 50, 70],
                  },
                  title: { text: null },
                  credits: { enabled: false },
                  exporting: { enabled: false },
                  xAxis: {
                    categories: monthlySpendData.map((d) => `${_t(d.month , d.month)} ${d.year}`),
                    labels: {
                      style: {
                        fontSize: "12px",
                        color: "#667085",
                        fontWeight: "500",
                      },
                      y: 18,
                    },
                    lineColor: "#e5e7eb",
                    tickColor: "#e5e7eb",
                  },
                  yAxis: {
                    min: 0,
                    title: { text: null },
                    labels: {
                      style: {
                        fontSize: "12px",
                        color: "#667085",
                        fontWeight: "500",
                      },
                      formatter: function () {
                        return "$" + (this.value / 1000).toFixed(0) + "K";
                      },
                      x: -8,
                    },
                    gridLineColor: "#f3f4f6",
                    gridLineWidth: 1,
                  },
                  legend: { enabled: false },
                  tooltip: {
                    formatter: function () {
                      return (
                        '<div style="background: #fff; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px;">' +
                        /* '<span style="color: #1d1f2c; font-weight: 600;">' +
                        this.x +
                        "</span><br/>" + */
                        '<span style="color: #fb923c; font-weight: 700;">$' +
                        this.y.toLocaleString() +
                        "</span></div>"
                      );
                    },
                    backgroundColor: "transparent",
                    borderWidth: 0,
                    shadow: false,
                  },
                  plotOptions: {
                    column: {
                      borderWidth: 0,
                      borderRadius: 4,
                      dataLabels: { enabled: false },
                      pointPadding: 0.3,
                      groupPadding: 0.15,
                    },
                  },
                  series: [
                    {
                      name: _t("monthly_expenses_series", "Monthly Expenses"),
                      data: monthlySpendData.map((item) => item.value),
                      color: "#fb923c",
                    },
                  ],
                }}
              />
            </div>
          </div>
        </div>
      </DashboardSection>
        </>
      )}
    </div>
  );
};

export default ExpenseDashboard;
